import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../lib/api-client';
import { useAuth } from '../lib/auth-context';
import { SkeletonRows } from '../components/ui/skeleton';

type Outcome = { kind: 'pending' } | { kind: 'error'; message: string };

/**
 * Mounted at the SPA route configured as ENTRA_REDIRECT_URI (see docs/frontend.md) —
 * Entra redirects the browser here with ?code&state after the user approves sign-in.
 * This page finishes the flow by POSTing to /v1/auth/sso/callback (a JSON API, not a
 * redirect target itself), which is why the redirect must land in the SPA rather than
 * directly at the API.
 *
 * A SSO_LOGIN_DENIED result (verified Microsoft identity, but no DefendIQ account/an
 * inactive one) is NOT rendered inline here — it navigates to `/login?error=<reason>` so the
 * user lands back on the login page itself with the explanation visible there (Login.tsx reads
 * and displays it), rather than staying stranded on this transitional callback route.
 */
export function SsoCallback() {
  const [params] = useSearchParams();
  const { completeSsoLogin } = useAuth();
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'pending' });

  useEffect(() => {
    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state) {
      setOutcome({ kind: 'error', message: 'This sign-in link is missing required information. Please try signing in again.' });
      return;
    }
    completeSsoLogin({ code, state })
      .then(() => navigate('/', { replace: true }))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.code === 'SSO_LOGIN_DENIED') {
          const reason = err.detail === 'MEMBERSHIP_DISABLED' ? 'disabled' : 'not_a_member';
          navigate(`/login?error=${reason}`, { replace: true });
          return;
        }
        setOutcome({ kind: 'error', message: 'Sign-in did not complete. Please try again.' });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (outcome.kind === 'pending') {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-4 text-center">
          <p className="text-sm text-ink-muted">Finishing sign-in…</p>
          <SkeletonRows rows={2} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-6 text-center">
      <p className="text-sm text-danger">{outcome.message}</p>
    </div>
  );
}
